import { ROLE_HOSPITAL_USER } from 'src/app/core/constants/user-management';
import { RowMapper } from '../utils/row-mapper';
import { Role } from './entitlements/role';
import { Individual } from './individual';
import { NewLinkedUser } from './new-linked-user';
import { Organization } from './organization';
import { USER_AFFILIATION_TYPE_TEMPLATE, UserAffiliation } from './user-affiliation';

export class User extends Individual {
  userroleId: number;
  userId: number;
  role: Role = null;
  organization: Organization;
  affiliation: UserAffiliation;
  firstName: string;
  lastName: string;
  nickName: string;
  password = '';
  passPhrase: string;
  userName: string;
  emailId: string;
  telePin: string;
  active: string;
  challengeQuestion: string = null;
  challengeAnswer: string;
  timezone: string = null;
  assignedBy: string;
  linkedUserId: number;
  healthPlanId: string;
  confirmPassword = '';
  selectedOption: string;
  employerId: number = null;
  employer: string;
  jobTitle: string;
  otherJobTitle: string; // free form title if user had no matches from list of titles
  linkedUsers: User[];
  hositalsFacilityGroupDescription: string;
  asmStatus: boolean;
  sudSmiProgramDashboardAccess: boolean;
  newLinkedUsers: NewLinkedUser[];
  medicalHomes: string[];
  fullFormTimezone: string;
}

export class UserForTag {
  id: number;
  label: string;
  name: string;
  text: string;
  value: string;
}

export class UserSearchRow {
  userId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  userName: string;
  email: string;
  roleDescription: string;
  affiliationDescription: string;
  roleId: number;
  active: string;
  organizationName: string;
  organizationDescription: string;
  orgLevel: number;
  orgId: number;
  employerId: number;
  employer: string;
  jobTitle: string;
  otherJobTitle: string;
  displayJobTitle: string;
  linkedUsers: UserSearchRow[];
  linkedUserId: number;
  hositalsFacilityGroupDescription: string;
  asmStatus: boolean;
  sudSmiProgramDashboardAccess: boolean;
  timezone: string;
  mobileNo: string;
  medicalHomes: string[];
}

function createAffiliationDescription(affiliation: UserAffiliation): string {
  if (!affiliation) {
    return 'Unknown'; // should never happen
  }

  if (affiliation.affiliationType === USER_AFFILIATION_TYPE_TEMPLATE) {
    return affiliation.affiliationType + ' - ' + affiliation.templateId;
  }

  return affiliation.affiliationType;
}

export class UserSearchRowMapper implements RowMapper<User, UserSearchRow> {
  mapRows(values: User[]): UserSearchRow[] {
    if (values?.length) {
      return values.map((v) => {
        return this.mapRow(v);
      });
    }

    return [];
  }

  mapRow(user: User) {
    const row = new UserSearchRow();
    row.userId = user.userId;
    row.firstName = user.firstName;
    row.lastName = user.lastName;
    row.fullName = user.firstName + ' ' + user.lastName;
    row.userName = user.userName;
    row.email = user.emailId;
    row.roleDescription = user.role?.roleDescription;
    row.mobileNo = user.mobileNo;
    row.linkedUserId = user.linkedUserId;

    if (!user.affiliation?.description) {
      row.affiliationDescription = createAffiliationDescription(user.affiliation);
    } else {
      row.affiliationDescription = user.affiliation.description;
    }

    if (user.organization) {
      row.organizationName = user.organization.orgName;
      row.organizationDescription = user.organization.orgDescription;
    }

    if (user.linkedUsers) {
      row.linkedUsers = this.mapRows(user.linkedUsers);
    }

    row.roleId = user.role?.roleId;
    row.active = user.active;

    row.orgLevel = user.orgLevel;
    row.orgId = user.orgId;

    row.employerId = user.employerId;
    row.employer = user.employer;
    row.jobTitle = user.jobTitle;
    row.otherJobTitle = user.otherJobTitle;
    row.asmStatus = user.asmStatus;
    row.sudSmiProgramDashboardAccess = user.sudSmiProgramDashboardAccess;
    row.medicalHomes = user.medicalHomes;

    if (row.jobTitle || row.otherJobTitle) {
      row.displayJobTitle = row.jobTitle + (row.otherJobTitle ? `: ${row.otherJobTitle}` : '');
    }

    if (row.roleDescription === ROLE_HOSPITAL_USER) {
      row.hositalsFacilityGroupDescription = user.hositalsFacilityGroupDescription;
    }

    return row;
  }
}
